/**
 * Persistent key-value storage that never throws.
 *
 * localStorage is not always usable: Safari in private mode and browsers with blocked site
 * data throw on access, and any browser throws when the quota is exceeded. The game must
 * keep working in all those cases (it just forgets things on reload), so every access goes
 * through a guard and falls back to memory.
 */

/** The subset of the Web Storage API the app uses. */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Storage keys. The version suffix is part of the key: when a stored shape changes
 * incompatibly, bump it and old data is simply ignored.
 */
export const STORAGE_KEYS = {
  session: 'cube-rush/session/v1',
  profile: 'cube-rush/profile/v1',
  settings: 'cube-rush/settings/v1',
  lastRoom: 'cube-rush/last-room/v1',
  /** The device's stable player key (statistics follow it across logins; kept on logout). */
  playerKey: 'cube-rush/player-key/v1',
} as const;

/** Non-persistent storage: fallback when localStorage is unusable, and for tests. */
export class MemoryStorage implements KeyValueStorage {
  private readonly items = new Map<string, string>();

  getItem(key: string): string | null {
    return this.items.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.items.set(key, value);
  }

  removeItem(key: string): void {
    this.items.delete(key);
  }
}

/** Wraps a storage so that failing calls are ignored (reads return null). */
export class GuardedStorage implements KeyValueStorage {
  constructor(private readonly inner: KeyValueStorage) {}

  getItem(key: string): string | null {
    try {
      return this.inner.getItem(key);
    } catch {
      return null;
    }
  }

  setItem(key: string, value: string): void {
    try {
      this.inner.setItem(key, value);
    } catch {
      // Quota exceeded or storage blocked: the value just is not persisted.
    }
  }

  removeItem(key: string): void {
    try {
      this.inner.removeItem(key);
    } catch {
      // See setItem.
    }
  }
}

/** The browser's localStorage if it works, otherwise memory. */
export function openStorage(): KeyValueStorage {
  try {
    const storage = globalThis.localStorage;
    const probe = 'cube-rush/probe';
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return new GuardedStorage(storage);
  } catch {
    return new MemoryStorage();
  }
}

/** Reads a JSON value; returns null when it is missing, corrupt or rejected by `parse`. */
export function readJson<T>(
  storage: KeyValueStorage,
  key: string,
  parse: (raw: unknown) => T | null,
): T | null {
  const text = storage.getItem(key);
  if (text === null) return null;
  try {
    return parse(JSON.parse(text));
  } catch {
    return null;
  }
}

export function writeJson(storage: KeyValueStorage, key: string, value: unknown): void {
  storage.setItem(key, JSON.stringify(value));
}
